import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { SupportedLanguage, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from './config';

interface LanguageContextType {
  currentLanguage: SupportedLanguage;
  changeLanguage: (language: SupportedLanguage) => void;
  isChangingLanguage: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Get current language from i18n
  const currentLanguage = (i18n.language || DEFAULT_LANGUAGE).split('-')[0] as SupportedLanguage;

  // Initialize language from URL or localStorage
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const langFromUrl = pathSegments[0];
    
    // Check if the first segment is a supported language
    if (SUPPORTED_LANGUAGES.includes(langFromUrl as SupportedLanguage)) {
      if (langFromUrl !== currentLanguage) {
        i18n.changeLanguage(langFromUrl);
      }
    } else {
      // No language in URL, check localStorage or use default
      const savedLanguage = localStorage.getItem('awasi-language') as SupportedLanguage;
      const languageToUse = savedLanguage && SUPPORTED_LANGUAGES.includes(savedLanguage) 
        ? savedLanguage 
        : DEFAULT_LANGUAGE;
      
      if (languageToUse !== currentLanguage) {
        i18n.changeLanguage(languageToUse);
      }
    }
  }, [location.pathname, i18n, currentLanguage]);

  // Change language handler
  const changeLanguage = useCallback(async (newLanguage: SupportedLanguage) => {
    if (newLanguage === currentLanguage) return;
    
    setIsChangingLanguage(true);
    
    try {
      // Change i18n language
      await i18n.changeLanguage(newLanguage);
      
      // Save to localStorage
      localStorage.setItem('awasi-language', newLanguage);
      
      // Don't navigate to language-prefixed URLs - just stay on current page
      // The language change will trigger re-renders with new translations
      
      // Update document language attribute
      document.documentElement.lang = newLanguage;
      
    } catch (error) {
      console.error('Error changing language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  }, [currentLanguage, i18n]);

  const value: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    isChangingLanguage
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};